import React, { useState } from 'react';
import { useCampaign } from '../../contexts/CampaignContext';
import { TELEPORT_STYLES, teleportStyleMeta } from '../../lib/teleports';

// DM-side panel listing every placed teleport with controls to edit style /
// one-vs-two-way / hidden + DC (trapdoors only) / label / delete.
// Mirrors TrapPanel's collapse-row pattern.

export default function TeleportPanel() {
  const { teleports, updateTeleport, deleteTeleport } = useCampaign();
  const [expandedId, setExpandedId] = useState(null);

  const list = Object.values(teleports || {}).sort(
    (a, b) => (a.createdAt || 0) - (b.createdAt || 0)
  );

  if (list.length === 0) {
    return (
      <div style={styles.panel}>
        <div style={styles.header}>
          <span style={styles.title}>🌀 Teleports</span>
        </div>
        <div style={styles.empty}>
          Place a teleport with the Hidden ▸ Teleport tool, then edit it here.
        </div>
      </div>
    );
  }

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <span style={styles.title}>🌀 Teleports</span>
        <span style={styles.count}>{list.length}</span>
      </div>
      <div style={styles.list}>
        {list.map((tp) => {
          const isOpen = expandedId === tp.id;
          const meta   = teleportStyleMeta(tp.style);
          const isTrapdoor = tp.style === 'trapdoor';
          return (
            <div key={tp.id} style={styles.row}>
              <button
                style={styles.rowHeader}
                onClick={() => setExpandedId(isOpen ? null : tp.id)}
              >
                <span style={{ ...styles.rowLabel, color: meta.color }}>
                  {meta.icon} {tp.label || meta.label}
                </span>
                <span style={styles.rowMeta}>
                  ({tp.a?.x},{tp.a?.y}){tp.twoWay ? '↔' : '→'}({tp.b?.x},{tp.b?.y})
                  {tp.hidden ? ' • hidden' : ''}
                </span>
                <span style={styles.chevron}>{isOpen ? '▾' : '▸'}</span>
              </button>
              {isOpen && (
                <div style={styles.body}>
                  <Row label="Label">
                    <input
                      style={styles.input}
                      value={tp.label || ''}
                      placeholder="(none)"
                      onChange={(e) => updateTeleport(tp.id, { label: e.target.value || null })}
                    />
                  </Row>
                  <Row label="Style">
                    <select
                      style={styles.input}
                      value={tp.style || 'portal'}
                      onChange={(e) => updateTeleport(tp.id, { style: e.target.value })}
                    >
                      {Object.entries(TELEPORT_STYLES).map(([id, def]) => (
                        <option key={id} value={id}>{def.label}</option>
                      ))}
                    </select>
                  </Row>
                  <Row label="Direction">
                    <label style={styles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={!!tp.twoWay}
                        onChange={(e) => updateTeleport(tp.id, { twoWay: e.target.checked })}
                      />
                      <span>{tp.twoWay ? 'Two-way (↔)' : 'One-way (A → B)'}</span>
                    </label>
                  </Row>
                  {isTrapdoor && (
                    <>
                      <Row label="Hidden">
                        <label style={styles.toggleLabel}>
                          <input
                            type="checkbox"
                            checked={!!tp.hidden}
                            onChange={(e) => updateTeleport(tp.id, {
                              hidden: e.target.checked,
                              // Auto-seed a reasonable DC when first marking hidden.
                              ...(e.target.checked && tp.discoveryDC == null ? { discoveryDC: 12 } : {}),
                            })}
                          />
                          <span>Hide until perceived</span>
                        </label>
                      </Row>
                      {tp.hidden && (
                        <Row label="Discovery DC">
                          <input
                            type="number"
                            style={{ ...styles.input, width: 70 }}
                            value={tp.discoveryDC ?? ''}
                            placeholder="12"
                            onChange={(e) => {
                              const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                              updateTeleport(tp.id, { discoveryDC: Number.isFinite(v) ? v : null });
                            }}
                          />
                        </Row>
                      )}
                    </>
                  )}
                  <div style={styles.actions}>
                    {tp.hidden && (
                      <button
                        className="btn-ghost btn-sm"
                        style={{ flex: 1 }}
                        onClick={() => updateTeleport(tp.id, { hidden: false })}
                        title="Reveal this hidden trapdoor to everyone"
                      >
                        👁 Reveal
                      </button>
                    )}
                    <button
                      className="btn-danger btn-sm"
                      style={{ flex: 1 }}
                      onClick={() => deleteTeleport(tp.id)}
                    >
                      🗑 Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={styles.fieldRow}>
      <label style={styles.fieldLabel}>{label}</label>
      <div style={styles.fieldInput}>{children}</div>
    </div>
  );
}

const styles = {
  panel:  { background: 'rgba(122,87,184,0.06)', border: '1px solid rgba(122,87,184,0.3)', borderRadius: 6, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title:  { fontSize: 12, fontFamily: "'Press Start 2P', monospace", color: '#c084fc', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 },
  count:  { fontSize: 11, color: '#9a8b6e', fontVariantNumeric: 'tabular-nums' },
  empty:  { fontSize: 11, color: '#9a8b6e', fontStyle: 'italic', lineHeight: 1.35 },
  list:   { display: 'flex', flexDirection: 'column', gap: 4 },
  row:    { background: '#0e1528', border: '1px solid #2a3552', borderRadius: 4, overflow: 'hidden' },
  rowHeader: { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', color: '#f0e6d0', textAlign: 'left' },
  rowLabel: { fontSize: 12, fontWeight: 700, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowMeta:  { fontSize: 10, color: '#9a8b6e', fontVariantNumeric: 'tabular-nums', flexShrink: 0 },
  chevron:  { fontSize: 10, color: '#9a8b6e' },
  body:     { padding: '8px 10px', borderTop: '1px solid #2a3552', display: 'flex', flexDirection: 'column', gap: 6 },
  fieldRow: { display: 'flex', alignItems: 'center', gap: 8 },
  fieldLabel:{ fontSize: 10, color: '#9a8b6e', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: 80 },
  fieldInput:{ display: 'flex', gap: 6, alignItems: 'center', flex: 1 },
  input:    { padding: '4px 6px', background: '#142038', border: '1px solid #4a3d28', borderRadius: 3, color: '#f0e6d0', fontSize: 12, fontFamily: 'system-ui, sans-serif' },
  toggleLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#f0e6d0', cursor: 'pointer' },
  actions:  { display: 'flex', gap: 6, marginTop: 4 },
};
